/**
 * The mailroom — a check, an envelope, and whoever it is for.
 *
 * This is a data-entry screen driven with a pile of opened post in front of
 * the operator, so it is laid out in the order the envelope is read rather
 * than in the order the API wants:
 *
 *   1. Who is the membership for — themselves, or a gift?
 *   2. The gift giver, or the member paying for themselves (the payer)?
 *   3. If a gift: who receives the months?
 *   4. The check itself.
 *
 * The gift question comes FIRST because gifts are the majority of the check
 * file — 1,776 of 3,221 active check subscriptions, plus 83 gift renewals —
 * and because it changes what every field below it means. A toggle buried at
 * the bottom would be read as an exception; it is the common case.
 *
 * Three things this screen must never do:
 *
 *   - Grant two terms for one check. Every save carries a `form_token` minted
 *     with `crypto.randomUUID()`; the backend refuses a token it has already
 *     seen. A fresh one is minted after each success, and the submit control
 *     is disabled while the mutation is in flight.
 *   - Silently adjust the term when a check is short. Short payments are
 *     routine (an old order form with last year's price). The screen names
 *     the shortfall and the months being granted; the operator decides.
 *   - Accept a mis-keyed ZIP quietly. Account number + ZIP is the whole
 *     sign-in for a member with no email address, which most check members
 *     are, so the field carries a visible hint saying so.
 *
 * "Months", never "issues" — house rule, and the truth of the product: a
 * SimpleCirc issue is a month of access.
 */
import {
  useEffect, useMemo, useRef, useState,
  type KeyboardEvent as ReactKeyboardEvent, type ReactNode,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  api,
  type MembershipPlan,
  type OfflineCandidate,
  type OfflineIntakeResult,
  type OfflineSubscriberResult,
} from '../api/client'
import { Panel } from '../components/Panel'
import { describeError } from '../lib/describeError'
import { useDebounced } from '../lib/useDebounced'
import { Field, Select } from '../components/Field'
import { Button } from '../components/Button'
import { PubBadge } from '../components/PubBadge'
import { fmtMoney } from '../lib/format'

/** What the lookup and the deep link hand back. Deliberately narrower than
 *  `Subscriber` in client.ts, which declares fields (`title`, `notes`) the
 *  subscriber-list serialiser does not actually return. */
type Member = {
  id: number
  first_name: string | null
  last_name: string | null
  email: string | null
  city: string | null
  state: string | null
  zipcode: string | null
  member_account_number: string | null
  simplecirc_account_id: string | null
  /** Only ever set for a member this screen just created. The subscriber
   *  serialiser does not return `company`, so a looked-up company member
   *  still shows as "(no name)" — noted in the task report. */
  company?: string | null
}

type MemberDraft = {
  title: string
  first_name: string
  last_name: string
  company: string
  address_1: string
  address_2: string
  city: string
  state: string
  zipcode: string
  country: string
  email: string
  phone: string
}

// 'UNITED STATES', not 'US': that is the spelling the existing 127,913 US
// rows carry, and a second spelling would quietly fragment the mailing file.
const EMPTY_DRAFT: MemberDraft = {
  title: '', first_name: '', last_name: '', company: '',
  address_1: '', address_2: '', city: '', state: '',
  zipcode: '', country: 'UNITED STATES', email: '', phone: '',
}

type Side = 'payer' | 'recipient'

function todayStr(): string {
  return new Date().toISOString().slice(0, 10)
}

/** A plain 'YYYY-MM-DD' from the API. Parsed with an explicit time so it
 *  lands on local midnight — `new Date('2027-09-15')` is UTC midnight, which
 *  renders as the 14th for an operator in Eastern time. */
function fmtDay(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

/** Either number signs a member in — the entitlement engine checks
 *  `member_account_number` first and falls through to the SimpleCirc id —
 *  so the one we quote is whichever they have. Same rule `/match` uses.
 *
 *  Except a placeholder. `offline-3f2a…`, `stripe-cus_…` and the rest are
 *  invented to fill a NOT NULL column for a buyer who never came from
 *  SimpleCirc; they are not account numbers, and an operator reading one
 *  off this screen cannot tell. Nothing beats a number that isn't one. */
function accountOf(m: Member): string {
  if (m.member_account_number) return m.member_account_number
  const id = m.simplecirc_account_id ?? ''
  if (/^(offline-|stripe-|authnet-|paypal)/.test(id)) return '—'
  return id || '—'
}

/** "12 months", "1 month" — and never "issues", which is what SimpleCirc
 *  calls the same thing. A member buys months of access. */
function months(n: number): string {
  return `${n} month${n === 1 ? '' : 's'}`
}

function nameOf(m: Member): string {
  const person = [m.first_name, m.last_name].filter(Boolean).join(' ').trim()
  return person || (m.company ?? '').trim() || '(no name)'
}

export function OfflineIntakePage() {
  const [params, setParams] = useSearchParams()
  const deepLinkId = params.get('subscriber')

  const [gift, setGift] = useState(false)

  const [payer, setPayer] = useState<Member | null>(null)
  const [payerDraft, setPayerDraft] = useState<MemberDraft>(EMPTY_DRAFT)
  const [payerNew, setPayerNew] = useState(false)

  const [recipient, setRecipient] = useState<Member | null>(null)
  const [recipientDraft, setRecipientDraft] = useState<MemberDraft>(EMPTY_DRAFT)
  const [recipientNew, setRecipientNew] = useState(false)

  const [publication, setPublication] = useState('')
  const [planId, setPlanId] = useState('')
  const [amount, setAmount] = useState('')
  const [checkNumber, setCheckNumber] = useState('')
  const [checkDate, setCheckDate] = useState(todayStr())
  const [receivedDate, setReceivedDate] = useState(todayStr())
  const [notes, setNotes] = useState('')
  // Off unless the order form says so. A cheque from a member we still
  // hold a card for extends the term and leaves the card alone; ticking
  // this is the operator relaying an instruction the member actually gave.
  const [stopAutoRenew, setStopAutoRenew] = useState(false)

  // One token per save. The recipient's `/subscriber` call gets its own
  // because it is a separate submission with a separate replay guard (that
  // route has no Order row to check, so it reads the audit trail instead).
  const [formToken, setFormToken] = useState(() => crypto.randomUUID())
  const [recipientToken, setRecipientToken] = useState(() => crypto.randomUUID())

  const [result, setResult] = useState<OfflineIntakeResult | null>(null)
  const [resultRecipient, setResultRecipient] = useState<Member | null>(null)

  // Task 11 links in here from the member record. Pre-select that member as
  // the payer so the operator lands on the check details.
  const deepLink = useQuery({
    queryKey: ['offline-deep-link', deepLinkId],
    queryFn: () => api.get<Member>(`/api/subscribers/${deepLinkId}`),
    enabled: Boolean(deepLinkId),
    staleTime: 60_000,
  })
  const deepLinked = deepLink.data
  useEffect(() => {
    if (deepLinked) setPayer(deepLinked)
  }, [deepLinked])

  const plans = useQuery({
    queryKey: ['membership-plans'],
    queryFn: () => api.get<{ plans: MembershipPlan[] }>('/api/admin/settings/plans'),
    staleTime: 10 * 60 * 1000,
  })
  const allPlans = useMemo(() => plans.data?.plans ?? [], [plans.data])

  const pubOptions = useMemo(() => {
    const slugs = [...new Set(allPlans.map((p) => p.publication_slug))].sort()
    return [{ value: '', label: '— choose a title —' }, ...slugs.map((s) => ({ value: s, label: PUB_LABEL[s] ?? s }))]
  }, [allPlans])

  const pubPlans = useMemo(
    () => allPlans
      .filter((p) => p.publication_slug === publication)
      .sort((a, b) => b.term_months - a.term_months),
    [allPlans, publication],
  )

  // A check almost always buys the longest term on offer, so the plan
  // pre-selects to it. Retired plans stay selectable — an old order form
  // names an old plan, and the backend accepts one deliberately.
  //
  // Only an EMPTY (or no-longer-valid) choice is filled in. TanStack
  // refetches the price list on window focus, which hands back a new array
  // identity; re-running the default here unconditionally would quietly
  // undo a plan the operator had already picked.
  useEffect(() => {
    if (!publication) { setPlanId(''); return }
    setPlanId((cur) => {
      if (cur && pubPlans.some((p) => p.plan_id === cur)) return cur
      const preferred = pubPlans.find((p) => p.active) ?? pubPlans[0]
      return preferred ? preferred.plan_id : ''
    })
  }, [publication, pubPlans])

  const plan = pubPlans.find((p) => p.plan_id === planId) ?? null

  // The amount is deliberately NOT pre-filled from list price. It is what
  // the check was written for, and the two disagree often enough that a
  // pre-filled figure left untouched would put the wrong number in the
  // order ledger — the one place the money has to be true.

  const amountCents = useMemo(() => {
    const n = Number(amount.replace(/[$,\s]/g, ''))
    if (!isFinite(n) || n <= 0) return null
    return Math.round(n * 100)
  }, [amount])

  const payerReady = payer !== null || (payerNew && draftUsable(payerDraft))
  const samePerson = gift && payer !== null && recipient !== null && payer.id === recipient.id

  const missing = useMemo(() => {
    const out: string[] = []
    if (!payerReady) out.push(gift ? 'the gift giver' : 'the member')
    if (gift && recipient === null) {
      out.push(recipientNew
        ? 'the recipient — add them before recording the check'
        : 'who the gift is for')
    }
    if (samePerson) out.push('a different recipient — the gift giver and the recipient are the same member')
    if (!publication) out.push('a title')
    if (!planId) out.push('a plan')
    if (amountCents === null) out.push('the check amount')
    if (!checkNumber.trim()) out.push('the check number')
    if (!checkDate) out.push('the date on the check')
    if (!receivedDate) out.push('the date it arrived')
    return out
  }, [payerReady, gift, recipient, recipientNew, samePerson, publication, planId, amountCents, checkNumber, checkDate, receivedDate])

  const save = useMutation({
    mutationFn: () => {
      const common = {
        form_token: formToken,
        publication_slug: publication,
        plan_id: planId,
        amount_cents: amountCents,
        check_number: checkNumber.trim(),
        check_date: checkDate,
        received_date: receivedDate,
        notes: notes.trim(),
        gift_recipient_subscriber_id: gift && recipient ? recipient.id : null,
      }
      if (payer) {
        // Only `/payment` takes it: `/member` creates the payer, so there
        // is no existing card arrangement for it to stop.
        return api.post<OfflineIntakeResult>('/api/offline/payment', {
          ...common, subscriber_id: payer.id, stop_auto_renew: stopAutoRenew,
        })
      }
      // A new payer is created and charged in one call.
      return api.post<OfflineIntakeResult>('/api/offline/member', {
        ...common,
        title: payerDraft.title.trim() || null,
        first_name: payerDraft.first_name.trim() || null,
        last_name: payerDraft.last_name.trim() || null,
        company: payerDraft.company.trim() || null,
        address_1: payerDraft.address_1.trim() || null,
        address_2: payerDraft.address_2.trim() || null,
        city: payerDraft.city.trim() || null,
        state: payerDraft.state.trim() || null,
        zipcode: payerDraft.zipcode.trim() || null,
        country: payerDraft.country.trim() || null,
        email: payerDraft.email.trim() || null,
        phone: payerDraft.phone.trim() || null,
      })
    },
    onSuccess: (res) => {
      setResult(res)
      setResultRecipient(gift ? recipient : null)
      // A new payer now exists; hold them so "same check, another title"
      // records against the member rather than creating a second one.
      if (!payer && res.subscriber_id) {
        setPayer({
          id: res.subscriber_id,
          first_name: payerDraft.first_name.trim() || null,
          last_name: payerDraft.last_name.trim() || null,
          email: payerDraft.email.trim() || null,
          city: payerDraft.city.trim() || null,
          state: payerDraft.state.trim() || null,
          zipcode: payerDraft.zipcode.trim() || null,
          company: payerDraft.company.trim() || null,
          member_account_number: res.member_account_number ?? null,
          simplecirc_account_id: null,
        })
        setPayerNew(false)
      }
      // Armed for the next save before the operator can reach for it. One
      // check covering several titles is a real case and the backend allows
      // it (the order key carries the title), but the replay guard rejects a
      // reused token — so each title needs its own.
      setFormToken(crypto.randomUUID())
    },
  })

  function resetCheck() {
    setPublication('')
    setPlanId('')
    setAmount('')
    setNotes('')
    // Cleared per title, not just per envelope. Card billing is held per
    // (member, title), so stopping it on the second title of one check is
    // a second decision — it should be ticked, not inherited.
    setStopAutoRenew(false)
    setResult(null)
    setResultRecipient(null)
    save.reset()
  }

  /** Same check, another title. Keeps the payer, the recipient and the
   *  check itself; clears only what is per-title. */
  function anotherTitle() {
    resetCheck()
  }

  /** Next envelope. Everything goes. */
  function nextEnvelope() {
    resetCheck()
    setGift(false)
    // Including the deep link. It outlives the state it seeded: clearing
    // `payer` while `?subscriber=` is still in the URL leaves the query
    // below ready to re-select the PREVIOUS envelope's member the moment
    // anything refetches it — this screen is driven all day, and the
    // result is a check attributed to the wrong person.
    if (deepLinkId) {
      const next = new URLSearchParams(params)
      next.delete('subscriber')
      setParams(next, { replace: true })
    }
    setPayer(null)
    setPayerDraft(EMPTY_DRAFT)
    setPayerNew(false)
    setRecipient(null)
    setRecipientDraft(EMPTY_DRAFT)
    setRecipientNew(false)
    setCheckNumber('')
    setCheckDate(todayStr())
    setReceivedDate(todayStr())
    setFormToken(crypto.randomUUID())
    setRecipientToken(crypto.randomUUID())
  }

  return (
    <div className="p-8 space-y-6 max-w-[1100px]">
      <header className="border-b border-ink-500 pb-6">
        <div className="label mb-1">Members · the morning's mail</div>
        <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
          Record a check
        </h1>
        <p className="mt-2 text-sm text-paper-dim max-w-[62ch]">
          One envelope at a time. The term granted comes from the plan, never
          from the amount on the check — if the two disagree the screen says
          so and leaves the decision to you.
        </p>
      </header>

      {result ? (
        <Receipt
          result={result}
          payer={payer}
          recipient={resultRecipient}
          plan={plan}
          publication={publication}
          onAnotherTitle={anotherTitle}
          onNextEnvelope={nextEnvelope}
        />
      ) : (
        // A real <form>, so Enter submits from any field — this screen is
        // keyed all day and a mouse trip per envelope adds up. Every other
        // control inside carries an explicit type="button": a bare <button>
        // in a form defaults to submit, and "Change" or "Not on file" must
        // not record a check.
        <form
          className="space-y-6"
          onSubmit={(e) => {
            e.preventDefault()
            if (missing.length === 0 && !save.isPending) save.mutate()
          }}
        >
          <Panel label="Who the membership is for">
            <div className="grid sm:grid-cols-2 gap-3">
              <ChoiceCard
                active={!gift}
                onClick={() => setGift(false)}
                title="Themselves"
                sub="The person who wrote the check is the member."
              />
              <ChoiceCard
                active={gift}
                onClick={() => setGift(true)}
                title="A gift for someone else"
                sub="The gift giver writes the check; the months go to the recipient."
              />
            </div>
          </Panel>

          <Panel label={gift ? 'Gift giver' : 'The member'}>
            {deepLink.isLoading && (
              <p className="text-paper-trace text-sm">Loading the member…</p>
            )}
            {deepLink.isError && (
              <p className="text-radar-red text-sm mb-3">
                Couldn't load member {deepLinkId} — {describeError(deepLink.error)}. Search for them below.
              </p>
            )}
            <MemberSide
              side="payer"
              member={payer}
              setMember={setPayer}
              isNew={payerNew}
              setIsNew={setPayerNew}
              draft={payerDraft}
              setDraft={setPayerDraft}
            />
          </Panel>

          {gift && (
            <Panel label="Who the gift is for" accent="cyan">
              <p className="text-sm text-paper-dim mb-4 max-w-[62ch]">
                The months land here. This member needs their own record before
                the check can be recorded, so a new recipient is saved first —
                they get an account number of their own, which is what goes on
                the gift card.
              </p>
              <MemberSide
                side="recipient"
                member={recipient}
                setMember={setRecipient}
                isNew={recipientNew}
                setIsNew={setRecipientNew}
                draft={recipientDraft}
                setDraft={setRecipientDraft}
                createToken={recipientToken}
                onCreated={(res) => {
                  setRecipient({
                    id: res.subscriber_id,
                    first_name: recipientDraft.first_name.trim() || null,
                    last_name: recipientDraft.last_name.trim() || null,
                    email: recipientDraft.email.trim() || null,
                    city: recipientDraft.city.trim() || null,
                    state: recipientDraft.state.trim() || null,
                    zipcode: recipientDraft.zipcode.trim() || null,
                    company: recipientDraft.company.trim() || null,
                    member_account_number: res.member_account_number,
                    simplecirc_account_id: null,
                  })
                  setRecipientNew(false)
                  setRecipientToken(crypto.randomUUID())
                }}
              />
              {samePerson && (
                <p className="mt-4 text-sm text-radar-red">
                  The payer and the recipient are the same member. A gift needs
                  two people — if they are buying for themselves, change the
                  choice at the top of the screen.
                </p>
              )}
            </Panel>
          )}

          <Panel label="The check">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <Select
                label="Title"
                value={publication}
                onChange={(e) => setPublication(e.target.value)}
                options={pubOptions}
              />
              <Select
                label="Plan"
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                options={
                  publication
                    ? pubPlans.map((p) => ({
                        value: p.plan_id,
                        label: `${p.plan_id} · ${months(p.term_months)} · ${fmtMoney(p.amount_cents / 100, false, 2)}${p.active ? '' : ' · retired'}`,
                      }))
                    : [{ value: '', label: '— choose a title first —' }]
                }
                disabled={!publication}
              />
              <div>
                <Field
                  label="Amount on the check"
                  inputMode="decimal"
                  placeholder="59.95"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <p className="mt-1.5 text-[11px] font-mono text-paper-trace">
                  {plan
                    ? `List price ${fmtMoney(plan.amount_cents / 100, false, 2)} · ${months(plan.term_months)}`
                    : 'List price shows once a plan is chosen'}
                </p>
              </div>
              <Field
                label="Check number"
                value={checkNumber}
                onChange={(e) => setCheckNumber(e.target.value)}
                placeholder="1042"
              />
              <Field
                label="Date on the check"
                type="date"
                value={checkDate}
                onChange={(e) => setCheckDate(e.target.value)}
              />
              <Field
                label="Date it arrived"
                type="date"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
              />
            </div>

            <div className="mt-5">
              <Field
                label="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="What the order form said, anything odd about the envelope"
              />
            </div>

            {payer && (
              <label className="mt-5 flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={stopAutoRenew}
                  onChange={(e) => setStopAutoRenew(e.target.checked)}
                  className="mt-1 accent-radar-red"
                />
                <span className="text-[13px] text-paper">
                  They asked to stop card renewals
                  <span className="block text-[12px] text-paper-trace">
                    For a member who mailed this check because they want off
                    card billing. Clears the card we hold and turns auto-renew
                    off. Leave it unticked otherwise — a check on its own just
                    adds time, and the card keeps renewing as before.
                  </span>
                </span>
              </label>
            )}

            {plan && amountCents !== null && (
              <PriceNote plan={plan} amountCents={amountCents} />
            )}
          </Panel>

          <div className="space-y-3">
            {save.isError && (
              <SaveError
                error={save.error}
                onFreshToken={() => { setFormToken(crypto.randomUUID()); save.reset() }}
                onNextEnvelope={nextEnvelope}
              />
            )}
            {missing.length > 0 && (
              <p className="text-sm text-paper-trace">
                Still needed: {missing.join('; ')}.
              </p>
            )}
            <Button
              size="lg"
              type="submit"
              disabled={missing.length > 0 || save.isPending}
            >
              {save.isPending ? 'Recording…' : 'Record this check'}
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}

const PUB_LABEL: Record<string, string> = {
  rc_car_action: 'RC Car Action',
  model_airplane_news: 'Model Airplane News',
  flight_journal: 'Flight Journal',
  diecast: 'Die Cast X',
}

/** The gift question, asked as two things you can pick rather than a
 *  checkbox — it decides what every field below it means, and gifts are the
 *  majority of this file, not an exception to it. */
function ChoiceCard({
  active, onClick, title, sub,
}: {
  active: boolean
  onClick: () => void
  title: string
  sub: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`text-left px-4 py-3 border transition-colors duration-150 ${
        active
          ? 'border-amber-glow bg-amber-wash text-paper'
          : 'border-ink-500 text-paper-dim hover:border-paper-trace hover:text-paper'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 shrink-0 ${active ? 'bg-amber-glow' : 'bg-ink-500'}`} />
        <span className="text-[14px]">{title}</span>
      </div>
      <div className="mt-1 pl-4 text-[12px] text-paper-trace">{sub}</div>
    </button>
  )
}

/** Enough of a postal record to mail a magazine to. Email is deliberately
 *  not required — most check members have none, which is exactly why the
 *  account number and the ZIP matter so much.
 *
 *  A surname OR a company satisfies the name. Companies and clubs buy gift
 *  subscriptions, and a check drawn on "Ashwood Aviation LLC" with a
 *  purchasing department's address has no surname to give; requiring one
 *  made those envelopes impossible to key at all. */
function draftUsable(d: MemberDraft): boolean {
  return Boolean(
    (d.last_name.trim() || d.company.trim()) &&
    d.address_1.trim() && d.city.trim() &&
    d.state.trim() && d.zipcode.trim(),
  )
}

// ---------- One side of the transaction: payer or recipient ----------

function MemberSide({
  side, member, setMember, isNew, setIsNew, draft, setDraft,
  createToken, onCreated,
}: {
  side: Side
  member: Member | null
  setMember: (m: Member | null) => void
  isNew: boolean
  setIsNew: (v: boolean) => void
  draft: MemberDraft
  setDraft: (d: MemberDraft) => void
  /** Recipient only: a new recipient is saved on its own, before the check. */
  createToken?: string
  onCreated?: (res: OfflineSubscriberResult) => void
}) {
  if (member) {
    return (
      <MemberSummary
        member={member}
        onChange={() => { setMember(null); setIsNew(false) }}
      />
    )
  }
  if (isNew) {
    return (
      <NewMemberForm
        side={side}
        draft={draft}
        setDraft={setDraft}
        createToken={createToken}
        onCreated={onCreated}
        onUseInstead={(m) => { setMember(m); setIsNew(false) }}
        onCancel={() => setIsNew(false)}
      />
    )
  }
  return (
    <MemberSearch
      side={side}
      onPick={setMember}
      onNew={() => setIsNew(true)}
    />
  )
}

function MemberSummary({ member, onChange }: { member: Member; onChange: () => void }) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div>
        <div className="text-[15px] text-paper">{nameOf(member)}</div>
        <div className="mt-1 font-mono text-[12px] text-paper-dim">
          Account {accountOf(member)}
          {member.zipcode && <> · ZIP {member.zipcode}</>}
        </div>
        <div className="mt-0.5 font-mono text-[12px] text-paper-trace">
          {[member.city, member.state].filter(Boolean).join(', ') || '—'}
          {' · '}
          {member.email || 'no email on file'}
        </div>
      </div>
      <Button variant="ghost" size="sm" type="button" onClick={onChange}>Change</Button>
    </div>
  )
}

function MemberSearch({
  side, onPick, onNew,
}: {
  side: Side
  onPick: (m: Member) => void
  onNew: () => void
}) {
  const [q, setQ] = useState('')
  // `term` is what is actually searched. It normally trails the box by the
  // debounce, but Enter sets it straight away — an operator who has finished
  // typing should not wait out a timer they cannot see.
  const [term, setTerm] = useState('')
  const debounced = useDebounced(q.trim(), 300)
  useEffect(() => { setTerm(debounced) }, [debounced])

  const listRef = useRef<HTMLDivElement | null>(null)

  const { data, isFetching } = useQuery({
    queryKey: ['offline-lookup', term],
    queryFn: () => api.get<{ items: Member[] }>(
      `/api/subscribers?q=${encodeURIComponent(term)}&limit=8`,
    ),
    enabled: term.length >= 2,
  })
  const items = data?.items ?? []

  const resultButtons = () => [
    ...(listRef.current?.querySelectorAll<HTMLButtonElement>('button[data-result]') ?? []),
  ]

  /** Enter runs the search and hands the keyboard to the results; a second
   *  Enter picks the one in focus. `preventDefault` matters for more than
   *  tidiness — this box sits inside the check form, and without it Enter
   *  would implicitly submit the check instead of searching. */
  function onLookupKey(e: ReactKeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter' && e.key !== 'ArrowDown') return
    e.preventDefault()
    if (e.key === 'Enter') setTerm(q.trim())
    resultButtons()[0]?.focus()
  }

  /** Up and down walk the list; Enter and Space select, which a real
   *  `<button>` already does for us. */
  function onListKey(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    const buttons = resultButtons()
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement)
    if (at < 0) return
    e.preventDefault()
    buttons[e.key === 'ArrowDown' ? at + 1 : at - 1]?.focus()
  }

  return (
    <div className="space-y-4">
      <Field
        label={side === 'payer' ? 'Account number, name or email' : "The recipient's account number, name or email"}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onClear={() => setQ('')}
        onKeyDown={onLookupKey}
        placeholder="57469168  ·  Lovelace  ·  ada@example.com"
        // Only the payer's box: when a gift is being keyed both lookups
        // mount in the same commit, and the second autofocus would pull the
        // cursor out from under whoever is typing in the first.
        autoFocus={side === 'payer'}
      />

      {term.length >= 2 && (
        <div className="border border-ink-500" ref={listRef} onKeyDown={onListKey}>
          {isFetching && items.length === 0 && (
            <p className="px-4 py-3 text-sm text-paper-trace">Looking…</p>
          )}
          {!isFetching && items.length === 0 && (
            <p className="px-4 py-3 text-sm text-paper-trace">
              Nobody on file matches that.
            </p>
          )}
          {items.map((m) => (
            <button
              key={m.id}
              type="button"
              data-result=""
              onClick={() => onPick(m)}
              className="w-full text-left px-4 py-2.5 border-b border-ink-700/40 last:border-b-0 hover:bg-ink-800/60 focus:bg-ink-800/60 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-amber-glow group"
            >
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-[13px] text-paper group-hover:text-amber-glow group-focus:text-amber-glow">
                  {nameOf(m)}
                </span>
                <span className="font-mono text-[11px] text-paper-dim">
                  {accountOf(m)}
                </span>
              </div>
              <div className="font-mono text-[11px] text-paper-trace">
                {[m.city, m.state, m.zipcode].filter(Boolean).join(' · ') || '—'}
                {m.email ? ` · ${m.email}` : ' · no email'}
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" type="button" onClick={onNew}>
          Not on file — add them
        </Button>
        <span className="text-[11px] text-paper-trace">
          Search by the account number on the mailing label when there is one.
          Enter searches and drops into the results; ↑ ↓ walk them.
        </span>
      </div>
    </div>
  )
}

// ---------- The create form, with a live duplicate check ----------

function NewMemberForm({
  side, draft, setDraft, createToken, onCreated, onUseInstead, onCancel,
}: {
  side: Side
  draft: MemberDraft
  setDraft: (d: MemberDraft) => void
  createToken?: string
  onCreated?: (res: OfflineSubscriberResult) => void
  onUseInstead: (m: Member) => void
  onCancel: () => void
}) {
  const set = (k: keyof MemberDraft) => (e: { target: { value: string } }) =>
    setDraft({ ...draft, [k]: e.target.value })

  // The duplicate warning runs on the postal identity as it is typed — this
  // is the guard that catches "searched for a misspelled name, found nothing,
  // started keying a second record for someone already on file". It needs a
  // surname plus at least one locating field before it means anything.
  const probe = useDebounced({
    first_name: draft.first_name.trim() || null,
    last_name: draft.last_name.trim() || null,
    address_1: draft.address_1.trim() || null,
    zipcode: draft.zipcode.trim() || null,
    email: draft.email.trim() || null,
    phone: draft.phone.trim() || null,
  }, 500)
  const probeReady = Boolean(probe.last_name && (probe.zipcode || probe.address_1 || probe.email))

  const match = useQuery({
    queryKey: ['offline-match', probe],
    queryFn: () => api.post<{ candidates: OfflineCandidate[] }>('/api/offline/match', probe),
    enabled: probeReady,
    staleTime: 30_000,
  })
  const candidates = match.data?.candidates ?? []

  const create = useMutation({
    mutationFn: () => api.post<OfflineSubscriberResult>('/api/offline/subscriber', {
      form_token: createToken,
      title: draft.title.trim() || null,
      first_name: draft.first_name.trim() || null,
      last_name: draft.last_name.trim() || null,
      company: draft.company.trim() || null,
      address_1: draft.address_1.trim() || null,
      address_2: draft.address_2.trim() || null,
      city: draft.city.trim() || null,
      state: draft.state.trim() || null,
      zipcode: draft.zipcode.trim() || null,
      country: draft.country.trim() || null,
      email: draft.email.trim() || null,
      phone: draft.phone.trim() || null,
    }),
    onSuccess: (res) => onCreated?.(res),
  })

  // A candidate carries only name, account number and ZIP. Adopting one
  // fetches the real record first — otherwise the summary above the check
  // would report "no email on file" for a member who has one, which is
  // exactly the kind of wrong thing an operator acts on.
  const adopt = useMutation({
    mutationFn: (id: number) => api.get<Member>(`/api/subscribers/${id}`),
    onSuccess: (m) => onUseInstead(m),
  })

  const usable = draftUsable(draft)

  /** The recipient is saved on its own button, so Enter here has to mean
   *  that button — left alone it would implicitly submit the check form
   *  this panel sits inside, which is the wrong action and the wrong
   *  moment. The payer's form has no separate save, so Enter there is
   *  meant to submit the check and is left to do it. */
  function onFieldKey(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Enter' || side !== 'recipient') return
    if (!(e.target instanceof HTMLInputElement)) return
    e.preventDefault()
    if (usable && !create.isPending) create.mutate()
  }

  return (
    <div className="space-y-5" onKeyDown={onFieldKey}>
      {candidates.length > 0 && (
        <div className="border border-amber-glow/50 bg-amber-wash px-4 py-3">
          <div className="label-amber mb-2">
            Already on file? {candidates.length === 1 ? 'One member looks' : `${candidates.length} members look`} like this
          </div>
          <div className="space-y-1.5">
            {candidates.map((c) => (
              <div key={c.subscriber_id} className="flex items-baseline justify-between gap-4">
                <span className="text-[13px] text-paper">
                  {c.name}
                  <span className="font-mono text-[11px] text-paper-dim">
                    {' '}· {c.account_number ?? 'no account number'}
                    {c.zipcode ? ` · ${c.zipcode}` : ''}
                  </span>
                </span>
                <button
                  type="button"
                  className="label-amber hover:underline shrink-0 disabled:opacity-40"
                  disabled={adopt.isPending}
                  onClick={() => adopt.mutate(c.subscriber_id)}
                >
                  Use this member instead →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <Field label="Honorific (optional)" value={draft.title} onChange={set('title')} placeholder="Dr." />
        <Field label="Company (optional)" value={draft.company} onChange={set('company')} />
        <div className="hidden lg:block" />
        <Field label="First name" value={draft.first_name} onChange={set('first_name')} />
        <Field label="Last name" value={draft.last_name} onChange={set('last_name')} />
        <Field label="Email (optional)" type="email" value={draft.email} onChange={set('email')} />
        <Field label="Address" value={draft.address_1} onChange={set('address_1')} />
        <Field label="Address line 2 (optional)" value={draft.address_2} onChange={set('address_2')} />
        <Field label="Phone (optional)" value={draft.phone} onChange={set('phone')} />
        <Field label="City" value={draft.city} onChange={set('city')} />
        <Field label="State" value={draft.state} onChange={set('state')} placeholder="CA" />
        <div>
          <Field label="ZIP" value={draft.zipcode} onChange={set('zipcode')} inputMode="numeric" />
          <p className="mt-1.5 text-[11px] text-amber-glow max-w-[34ch]">
            Half of this member's sign-in — account number + ZIP. With no
            email address on the record it is the only way in, so a mis-keyed
            ZIP locks them out of what they just paid for.
          </p>
        </div>
        <Field label="Country" value={draft.country} onChange={set('country')} />
      </div>

      {(create.isError || adopt.isError) && (
        <p className="border border-radar-red/50 bg-radar-red/10 px-4 py-3 text-sm text-radar-red">
          {describeError(create.error ?? adopt.error)}
        </p>
      )}

      <div className="flex items-center gap-3">
        {side === 'recipient' ? (
          <Button
            size="sm"
            type="button"
            disabled={!usable || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Saving…' : 'Save the recipient'}
          </Button>
        ) : (
          <span className="text-[11px] text-paper-trace">
            {usable
              ? 'Saved together with the check below.'
              : 'A surname or a company, plus address, city, state and ZIP — enough to mail a magazine to.'}
          </span>
        )}
        <Button variant="ghost" size="sm" type="button" onClick={onCancel}>
          Search again instead
        </Button>
      </div>
    </div>
  )
}

// ---------- A refused save, and the way out of it ----------

/**
 * Both 409s this screen can hit read alike and mean different things, and
 * neither used to offer anything but a page reload — which mid-batch costs
 * the operator the envelope they were on.
 *
 *   - The order-key conflict (`record_check_payment`) means this physical
 *     check is already in the system, under the order key the message names.
 *     There is nothing to retry; the way on is the next envelope.
 *   - The replay guard (`_reject_replay`) means this FORM already went
 *     through — an Order somewhere carries this token. Usually the same
 *     thing, so the next envelope is still offered first. But a fresh token
 *     is safe to offer beside it: the order key is (payer, check number,
 *     title), so if the check really was recorded the retry lands on the
 *     conflict above and says so by name. It cannot grant a second term.
 */
function SaveError({
  error, onFreshToken, onNextEnvelope,
}: {
  error: unknown
  onFreshToken: () => void
  onNextEnvelope: () => void
}) {
  const message = describeError(error)
  // Both strings are the backend's own, in aamcirc/payments/offline.py and
  // aamcirc/admin/routers/offline_intake.py.
  const alreadyRecorded = message.includes('has already been recorded')
  const replayed = message.includes('this form was already submitted')

  return (
    <div className="border border-radar-red/50 bg-radar-red/10 px-4 py-3 space-y-3">
      <p className="text-sm text-radar-red">{message}</p>
      {alreadyRecorded && (
        <p className="text-sm text-paper-dim">
          Nothing is missing — this check is on the member's record already.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" type="button" onClick={onNextEnvelope}>
          Start the next envelope
        </Button>
        {replayed && !alreadyRecorded && (
          <>
            <Button variant="ghost" size="sm" type="button" onClick={onFreshToken}>
              Try again on a fresh form
            </Button>
            <span className="text-[11px] text-paper-trace max-w-[46ch]">
              Keeps everything you have keyed. If the check did go through,
              the retry will say so and name the order.
            </span>
          </>
        )}
      </div>
    </div>
  )
}

// ---------- List price, and what happens when the check disagrees ----------

function PriceNote({ plan, amountCents }: { plan: MembershipPlan; amountCents: number }) {
  const diff = amountCents - plan.amount_cents
  if (diff === 0) return null
  const term = months(plan.term_months)
  const gap = fmtMoney(Math.abs(diff) / 100, false, 2)
  const list = fmtMoney(plan.amount_cents / 100, false, 2)
  return (
    <p className={`mt-5 text-sm ${diff < 0 ? 'text-amber-glow' : 'text-paper-dim'}`}>
      {diff < 0
        ? `Check is ${gap} short of the ${list} list price. You are granting ${term}.`
        : `Check is ${gap} over the ${list} list price. You are granting ${term}.`}
      {' '}The term comes from the plan — change the plan if that is wrong.
    </p>
  )
}

// ---------- What to write on the acknowledgement ----------

function Receipt({
  result, payer, recipient, plan, publication, onAnotherTitle, onNextEnvelope,
}: {
  result: OfflineIntakeResult
  payer: Member | null
  recipient: Member | null
  plan: MembershipPlan | null
  publication: string
  onAnotherTitle: () => void
  onNextEnvelope: () => void
}) {
  // Whoever the months landed on is whoever will be signing in.
  const member = recipient ?? payer
  const bigNumber = member ? accountOf(member) : (result.member_account_number ?? '—')

  return (
    <Panel label="Recorded" accent="green">
      <div className="flex flex-wrap items-start gap-10">
        <div>
          <div className="label mb-2">
            {recipient ? "Recipient's account number" : 'Account number'}
          </div>
          <div
            className="font-mono font-semibold text-6xl text-amber-glow leading-none"
            style={{ fontVariantNumeric: 'tabular-nums slashed-zero' }}
          >
            {bigNumber}
          </div>
          <p className="mt-3 text-sm text-paper-dim max-w-[38ch]">
            This goes on the acknowledgement. With the ZIP
            {member?.zipcode ? ` (${member.zipcode})` : ''} it is the whole
            sign-in for a member with no email address.
          </p>
        </div>

        <div className="space-y-3 text-[13px]">
          <KV k="Title" v={publication ? <PubBadge slug={publication} /> : '—'} />
          <KV k="Paid through" v={fmtDay(result.current_period_end)} />
          <KV
            k="Granted"
            v={plan ? months(plan.term_months) : '—'}
          />
          <KV k="Taken" v={fmtMoney(result.amount_paid, false, 2)} />
          <KV k="List price" v={fmtMoney(result.amount_due, false, 2)} />
          <KV k="Order" v={<span className="font-mono text-[11px]">{result.order_key}</span>} />
          {recipient && payer && (
            <KV k="Gift from" v={`${nameOf(payer)} · ${accountOf(payer)}`} />
          )}
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-ink-500 pt-5">
        <Button variant="outline" onClick={onAnotherTitle}>
          Same check, another title
        </Button>
        <Button onClick={onNextEnvelope}>Next envelope</Button>
        <span className="text-[11px] text-paper-trace max-w-[46ch]">
          One check can cover several titles — each title is its own order, so
          record it again with the same check number under the new title.
        </span>
      </div>
    </Panel>
  )
}

function KV({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="label w-[92px] shrink-0">{k}</span>
      <span className="text-paper">{v}</span>
    </div>
  )
}
